import { reducedMotion } from './utils';
import { recordWatched } from './watched';

export function initVideoTabs(): void {
  const rootEl = document.querySelector<HTMLElement>('[data-video-tabs]');
  if (!rootEl) return;

  // Non-null local so the closures below don't need repeated guards.
  const root = rootEl;

  const tabEls = Array.from(root.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  const panelEls = Array.from(root.querySelectorAll<HTMLElement>('[role="tabpanel"]'));

  if (tabEls.length < 1 || panelEls.length < 1) return;

  // Guards the SCSS rule that hides inactive panels; without it all panels show (no-JS state).
  root.setAttribute('data-enhanced', '');

  let activeIndex = tabEls.findIndex((t) => t.getAttribute('aria-selected') === 'true');
  if (activeIndex === -1) activeIndex = 0;

  function syncState(nextIndex: number): void {
    tabEls.forEach((tab, i) => {
      const isActive = i === nextIndex;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panelEls.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === nextIndex);
    });
  }

  function activate(nextIndex: number, moveFocus: boolean): void {
    if (nextIndex === activeIndex) return;

    root.setAttribute('data-switched', '');

    activeIndex = nextIndex;
    syncState(activeIndex);

    if (moveFocus) {
      tabEls[activeIndex]?.focus();
    }
  }

  // No animation on init — content should appear instantly at page load.
  syncState(activeIndex);

  tabEls.forEach((tab, i) => {
    tab.addEventListener('click', () => {
      activate(i, false);
    });
  });

  // APG pattern: arrow keys move focus (roving tabindex), Enter/Space activates.
  const tablist = root.querySelector<HTMLElement>('[role="tablist"]');
  if (!tablist) return;

  tablist.addEventListener('keydown', (e: KeyboardEvent) => {
    const focused = document.activeElement;
    const focusedIndex = tabEls.indexOf(focused as HTMLButtonElement);
    if (focusedIndex === -1) return;

    const total = tabEls.length;

    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const next = (focusedIndex + 1) % total;
        tabEls[next].focus();
        tabEls[next].setAttribute('tabindex', '0');
        if (focusedIndex !== activeIndex) {
          tabEls[focusedIndex].setAttribute('tabindex', '-1');
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const prev = (focusedIndex - 1 + total) % total;
        tabEls[prev].focus();
        tabEls[prev].setAttribute('tabindex', '0');
        if (focusedIndex !== activeIndex) {
          tabEls[focusedIndex].setAttribute('tabindex', '-1');
        }
        break;
      }
      case 'Home': {
        e.preventDefault();
        tabEls[0].focus();
        if (focusedIndex !== activeIndex) {
          tabEls[focusedIndex].setAttribute('tabindex', '-1');
        }
        tabEls[0].setAttribute('tabindex', '0');
        break;
      }
      case 'End': {
        e.preventDefault();
        tabEls[total - 1].focus();
        if (focusedIndex !== activeIndex) {
          tabEls[focusedIndex].setAttribute('tabindex', '-1');
        }
        tabEls[total - 1].setAttribute('tabindex', '0');
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        activate(focusedIndex, true);
        break;
      }
    }
  });
}

/**
 * Flags the panel bodies that actually overflow, so the bottom fade only claims
 * its landing gap where the panel scrolls. That gap is part of the scrollable
 * content, so applying it unconditionally would hand a panel whose text already
 * fits a scrollbar for nothing but empty space.
 *
 * Where scroll-driven animations aren't supported (Firefox), it also drives the
 * fade's clear-out from a scroll listener — CSS handles that in Chrome/Safari
 * via `animation-timeline: scroll()`. Without it the ramp sat over the last
 * lines of the summary (the interviewee list) permanently.
 */
export function initPanelFades(): void {
  const bodies = Array.from(document.querySelectorAll<HTMLElement>('.video__panel-body'));
  if (bodies.length < 1) return;

  const hasScrollTimeline = CSS.supports('animation-timeline', 'scroll()');

  function measure(body: HTMLElement): void {
    // Inactive panels are display:none, so leave the last verdict alone until
    // the ResizeObserver reports them at a real size again.
    if (body.clientHeight === 0) return;

    // The fade's own contribution, backed out so the verdict reads the content
    // height alone — otherwise applying the gap would feed its own next
    // measurement and the class could oscillate.
    const fade = getComputedStyle(body, '::after');
    const gap = (parseFloat(fade.height) || 0) + (parseFloat(fade.marginTop) || 0);

    body.classList.toggle('is-scrollable', body.scrollHeight - gap > body.clientHeight);
  }

  // Mirrors the CSS animation-range: fully opaque until the last --fade-size of
  // scroll, then ramps to clear so the final lines are never read through it.
  function trackScroll(body: HTMLElement): void {
    const size = parseFloat(getComputedStyle(body, '::after').height) || 0;
    const remaining = body.scrollHeight - body.clientHeight - body.scrollTop;
    const opacity = size > 0 ? Math.min(1, Math.max(0, remaining / size)) : 1;

    body.style.setProperty('--fade-opacity', opacity.toFixed(3));
  }

  const observer = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const body = entry.target as HTMLElement;
      measure(body);
      if (!hasScrollTimeline) trackScroll(body);
    });
  });

  bodies.forEach((body) => {
    measure(body);
    observer.observe(body);

    if (hasScrollTimeline) return;
    trackScroll(body);
    body.addEventListener('scroll', () => trackScroll(body), { passive: true });
  });

  // Late-loading webfonts reflow the text without resizing the panel itself.
  document.fonts?.ready.then(() => bodies.forEach(measure));
}

export interface VimeoPlayer {
  setCurrentTime(seconds: number): Promise<number>;
  play(): Promise<void>;
  on(event: 'timeupdate', cb: (data: { seconds: number }) => void): void;
  on(event: 'ended', cb: () => void): void;
}
interface VimeoNamespace {
  Player: new (el: HTMLElement | string, options?: Record<string, unknown>) => VimeoPlayer;
}
declare global {
  interface Window {
    Vimeo?: VimeoNamespace;
  }
}

// Resolves once initVideoPlayer() has either constructed a player or given up
// (no embed on this page, or the SDK never loaded) — resolves null in the
// latter cases. Lets other entry points (e.g. search-highlight.ts) await the
// same player instance without re-running the SDK bootstrap themselves.
let resolvePlayerReady: (player: VimeoPlayer | null) => void;
const playerReady: Promise<VimeoPlayer | null> = new Promise((resolve) => {
  resolvePlayerReady = resolve;
});

export function getVideoPlayer(): Promise<VimeoPlayer | null> {
  return playerReady;
}

// Polls until the deferred Vimeo SDK is available, or resolves null after a timeout.
function whenVimeoReady(timeoutMs = 8000): Promise<VimeoNamespace | null> {
  if (window.Vimeo) return Promise.resolve(window.Vimeo);

  return new Promise((resolve) => {
    const start = Date.now();
    const poll = window.setInterval(() => {
      if (window.Vimeo) {
        window.clearInterval(poll);
        resolve(window.Vimeo);
      } else if (Date.now() - start > timeoutMs) {
        window.clearInterval(poll);
        resolve(null);
      }
    }, 50);
  });
}

export async function initVideoPlayer(): Promise<void> {
  const mount = document.querySelector<HTMLElement>('.video__player[data-vimeo-id]');
  if (!mount) {
    resolvePlayerReady(null);
    return;
  }

  const id = Number(mount.dataset.vimeoId);
  if (!id) {
    resolvePlayerReady(null);
    return;
  }

  const Vimeo = await whenVimeoReady();
  if (!Vimeo) {
    resolvePlayerReady(null); // SDK blocked/failed — <noscript> embed remains the fallback.
    return;
  }

  const player = new Vimeo.Player(mount, { id, title: false, byline: false, portrait: false });

  // Flag the video as watched once it reaches the end, so its cards pick up
  // the "watched" treatment sitewide.
  player.on('ended', () => recordWatched(String(id)));

  initTranscript(player);
  resolvePlayerReady(player);
}

function initTranscript(player: VimeoPlayer): void {
  const container = document.querySelector<HTMLElement>('[data-transcript]');
  if (!container) return;

  const cueEls = Array.from(container.querySelectorAll<HTMLButtonElement>('.transcript__seek'));
  if (cueEls.length === 0) return;

  const starts = cueEls.map((el) => Number(el.dataset.start) || 0);
  const smooth = !reducedMotion();

  cueEls.forEach((el, i) => {
    el.addEventListener('click', () => {
      player
        .setCurrentTime(starts[i])
        .then(() => player.play())
        .catch(() => {
          /* seeking before metadata loads can reject; ignore. */
        });
    });
  });

  // A manual scroll pauses auto-follow, so a programmatic smooth scroll can't
  // fight a thumb that's already moving the list.
  const FOLLOW_PAUSE_MS = 4000;
  let userScrolledAt = 0;
  let selfScrolling = false;

  container.addEventListener(
    'scroll',
    () => {
      if (!selfScrolling) userScrolledAt = Date.now();
    },
    { passive: true }
  );

  // Manual scroll math so scrollIntoView doesn't yank the whole page during playback.
  function scrollCueIntoView(el: HTMLElement): void {
    const c = container!;

    // Below $bp-desktop the transcript flows into the page rather than owning a
    // scroll area of its own (see _video.scss); following the cue there would
    // drag the player off screen.
    if (c.scrollHeight <= c.clientHeight) return;
    if (Date.now() - userScrolledAt < FOLLOW_PAUSE_MS) return;

    const cRect = c.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    if (eRect.top < cRect.top || eRect.bottom > cRect.bottom) {
      const offset = eRect.top - cRect.top + c.scrollTop - (c.clientHeight - el.offsetHeight) / 2;
      selfScrolling = true;
      c.scrollTo({ top: offset, behavior: smooth ? 'smooth' : 'auto' });
      // Release after the smooth animation settles, so its own scroll events
      // aren't mistaken for the user's.
      window.setTimeout(() => {
        selfScrolling = false;
      }, smooth ? 700 : 0);
    }
  }

  let currentIndex = -1;
  player.on('timeupdate', ({ seconds }) => {
    let idx = -1;
    for (let i = 0; i < starts.length; i++) {
      if (starts[i] <= seconds) idx = i;
      else break;
    }
    if (idx === currentIndex) return;
    currentIndex = idx;

    cueEls.forEach((el, i) => el.classList.toggle('is-current', i === idx));
    if (idx >= 0) scrollCueIntoView(cueEls[idx]);
  });
}

/**
 * Seeks the player to a transcript cue's start time and marks it current,
 * without starting playback — the same "jump to this point" as clicking the
 * cue, minus the auto-play. Used when a search result lands mid-transcript
 * (see initSearchHighlight() in search-highlight.ts).
 */
export function seekTranscriptCue(player: VimeoPlayer, cueEl: HTMLElement): void {
  const start = Number(cueEl.dataset.start) || 0;
  player.setCurrentTime(start).catch(() => {
    /* seeking before metadata loads can reject; ignore. */
  });

  cueEl.closest('[data-transcript]')
    ?.querySelectorAll<HTMLElement>('.transcript__seek')
    .forEach((el) => el.classList.toggle('is-current', el === cueEl));
}
